import AsyncStorage from './StorageHandler';
import { projectConfig } from '../project-info/projectConfig';

class TenantProjectInfo {
  constructor() {
    this.isLoading = true;
    this.isLoaded = false;
    this.hasGroups = null;
    this.hasChallenges = null;
    this.hasFitness = null;
    this.hasManualWorkouts = null;
    this.eventCategories = null;
    this.eventFilterLozenges = null;
    this.orgName = null;
    // maybe adding colors, privacy policy, waiver
    this.primaryColor = null;
    this.secondaryColor = null;
    this.tertiaryColor = null;
    this.accentColor = null;
    this.communityMemberPostingOnly = null; // limit posting to the community member/brand profile, and hide likes, comments, etc.
    this.logoUrl = null;
  }

  init() {
    return this._loadTenantProjectInfo();
  }

  _updateTenantProjectInfo(data) {
    let formattedEventCategories = data?.event_categories?.reduce((acc, category) => {
      acc[category.slug] = category;
      return acc;
    }, {}) || {};
    formattedEventCategories = Object.fromEntries(Object.entries(formattedEventCategories).sort((a, b) => a[1].slug.localeCompare(b[1].slug)));
    const eventLozenges = Object.values(formattedEventCategories).filter((category) => {
      return data.event_lozenges.includes(category.slug);
    });
    this.hasGroups = data?.group_count > 0 || false;
    this.hasChallenges = data?.enable_challenges || false;
    this.hasFitness = Object.prototype.hasOwnProperty.call(data || {}, 'enable_fitness')
      ? !!data.enable_fitness
      : null;
    this.hasManualWorkouts = Object.prototype.hasOwnProperty.call(
      data || {},
      'enable_manual_workouts',
    )
      ? !!data.enable_manual_workouts
      : null;
    // format event categories to match the mapping of event categories in eventConfig.js
    // this is used for local and virtual for both groups and not groups
    this.eventCategories = formattedEventCategories || {};
    this.eventFilterLozenges = eventLozenges || [];
    this.orgName = data?.name || '';
    this.primaryColor = data?.primary_color || '';
    this.secondaryColor = data?.secondary_color || '';
    this.tertiaryColor = data?.tertiary_color || '';
    this.accentColor = data?.accent_color || '';
    this.communityMemberPostingOnly = !data?.enable_social || false;
    this.logoUrl = data?.logo_url || null;
    // Set CSS variables after updating tenant info
    this._setCSSVariables();
  }

  // New method to set CSS variables
  _setCSSVariables() {
    // Only applicable on web; React Native has no document
    if (typeof document === 'undefined' || !document?.documentElement?.style) {
      return;
    }
    // Map tenant colors to CSS variables with fallbacks
    const colorMappings = {
      '--primary': [this.primaryColor, projectConfig.primaryColorOne],
      '--secondary': [this.secondaryColor, projectConfig.primaryColorTwo],
      '--primaryColorTwo': [this.tertiaryColor, projectConfig.secondaryColorTwo],
      '--accent': [this.accentColor, projectConfig.secondaryColorOne]
    };

    Object.entries(colorMappings).forEach(([cssVar, colors]) => {
      const [tenantColor, fallbackColor] = colors;
      const colorValue = tenantColor || fallbackColor;
      
      if (colorValue) {
        document.documentElement.style.setProperty(cssVar, colorValue);
      }
    });
  }

  saveToTenantProjectInfo(value) {
    this._updateTenantProjectInfo(value);
    AsyncStorage.setItem('tenantProjectInfo', JSON.stringify(value));
  }

  _loadTenantProjectInfo() {
    return AsyncStorage.getItem('tenantProjectInfo')
      .then(value => {
        if (value) {
          this._updateTenantProjectInfo(JSON.parse(value));
        }
        this.isLoading = false;
        this.isLoaded = true;
        return this;
      })
      .catch(error => {
        console.warn('Error loading tenant project info:', error);
        this.isLoading = false;
        this.isLoaded = true;
        return this;
      });
  }

  hasValidValue(key) {
    return this.hasOwnProperty(key) && this[key] !== null && this[key] !== undefined && this[key] !== '';
  }

  getValue(key) {
    return this.hasValidValue(key) ? this[key] : null;
  }
  
  clearStoredValues() {
    this.hasGroups = null;
    this.hasChallenges = null;
    this.hasFitness = null;
    this.hasManualWorkouts = null;
    this.eventCategories = null;
    this.eventFilterLozenges = null;
    this.orgName = null;
    this.primaryColor = null;
    this.secondaryColor = null;
    this.tertiaryColor = null;
    this.accentColor = null;
    this.logoUrl = null;
    this._setCSSVariables();
  }
}

export let tenantProjectInfo = new TenantProjectInfo();
tenantProjectInfo.init();
